import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Grid2,
	Paper,
	TextField,
} from "@mui/material";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { CatalogCustomer } from "@/shared/models/CatalogCustomer";
import type { CatalogGrade } from "@/shared/models/CatalogGrade";
import type { CatalogSection } from "@/shared/models/CatalogSection";
import { getCatalogGrades } from "@/shared/repositories/CatalogGradeRepository";
import { STATUS_COLORS, STATUS_OPTIONS } from "./statusConstants";

const ALL_CUSTOMER: CatalogCustomer = {
	CustomerId: -1,
	CustomerName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

const ALL_SECTION: CatalogSection = {
	SectionId: -1,
	SectionName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

const ALL_GRADE: CatalogGrade = {
	GradeId: -1,
	GradeName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

export const FILTERS_STORAGE_KEY = "workOrderFilters";

type StoredFilters = {
	customerId: number | null;
	sectionId: number | null;
	gradeId: number | null;
	statuses: string[];
};

export function loadStoredFilters(): StoredFilters | null {
	try {
		const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function saveStoredFilters(filters: StoredFilters) {
	localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
}

type Props = {
	customers: CatalogCustomer[];
	sections: CatalogSection[];
	onFilterChange: (filters: WorkOrderFiltersState) => void;
};

export type WorkOrderFiltersState = {
	customer: CatalogCustomer | null;
	section: CatalogSection | null;
	gradeName: string | null;
	statuses: string[];
};

export const WorkOrderFilters = ({
	customers,
	sections,
	onFilterChange,
}: Props) => {
	const stored = useRef(loadStoredFilters());

	const [customer, setCustomer] = useState<CatalogCustomer | null>(null);
	const [section, setSection] = useState<CatalogSection | null>(null);
	const [grade, setGrade] = useState<CatalogGrade | null>(null);
	const [statuses, setStatuses] = useState<string[]>(
		stored.current?.statuses ?? [],
	);

	const { data: grades } = useQuery<CatalogGrade[], Error>({
		queryKey: ["catalogGrades"],
		queryFn: () => getCatalogGrades(),
	});

	const notify = useCallback(
		(patch: Partial<WorkOrderFiltersState>) => {
			const next: WorkOrderFiltersState = {
				customer,
				section,
				gradeName: grade?.GradeName ?? null,
				statuses,
				...patch,
			};
			onFilterChange(next);
		},
		[customer, section, grade, statuses, onFilterChange],
	);

	// Restore customer/section from localStorage once data is available
	const restoredRef = useRef({ customer: false, section: false, grade: false });

	useEffect(() => {
		if (restoredRef.current.customer || !stored.current?.customerId) return;
		const match = customers.find(
			(c) => c.CustomerId === stored.current!.customerId,
		);
		if (match) {
			restoredRef.current.customer = true;
			setCustomer(match);
			notify({ customer: match });
		}
	}, [customers, notify]);

	useEffect(() => {
		if (restoredRef.current.section || !stored.current?.sectionId) return;
		const match = sections.find(
			(s) => s.SectionId === stored.current!.sectionId,
		);
		if (match) {
			restoredRef.current.section = true;
			setSection(match);
			notify({ section: match });
		}
	}, [sections, notify]);

	// Restore grade from localStorage
	useEffect(() => {
		if (restoredRef.current.grade || !stored.current?.gradeId || !grades)
			return;
		const match = grades.find((g) => g.GradeId === stored.current!.gradeId);
		if (match) {
			restoredRef.current.grade = true;
			setGrade(match);
			notify({ gradeName: match.GradeName });
		}
	}, [grades, notify]);

	// Notify parent of stored statuses on mount
	const initialNotified = useRef(false);
	useEffect(() => {
		if (initialNotified.current) return;
		if ((stored.current?.statuses?.length ?? 0) > 0) {
			initialNotified.current = true;
			notify({
				statuses: stored.current?.statuses ?? [],
			});
		}
	}, [notify]);

	// Persist filters to localStorage
	useEffect(() => {
		saveStoredFilters({
			customerId: customer?.CustomerId ?? null,
			sectionId: section?.SectionId ?? null,
			gradeId: grade?.GradeId ?? null,
			statuses,
		});
	}, [customer, section, grade, statuses]);

	const hasFilters = !!(customer || section || grade || statuses.length > 0);

	const clearFilters = () => {
		setCustomer(null);
		setSection(null);
		setGrade(null);
		setStatuses([]);
		onFilterChange({
			customer: null,
			section: null,
			gradeName: null,
			statuses: [],
		});
	};

	return (
		<Paper
			elevation={1}
			sx={{
				padding: 2,
				display: "flex",
				alignItems: "center",
			}}
		>
			<Grid2 container style={{ alignItems: "center" }} spacing={2}>
				<Grid2 size={{ xs: 2 }}>
					<Autocomplete
						size="small"
						options={[ALL_CUSTOMER, ...customers]}
						getOptionLabel={(opt) => opt.CustomerName}
						value={customer ?? ALL_CUSTOMER}
						isOptionEqualToValue={(opt, val) =>
							opt.CustomerId === val.CustomerId
						}
						onChange={(_, val) => {
							const mapped = !val || val.CustomerId === -1 ? null : val;
							setCustomer(mapped);
							notify({ customer: mapped });
						}}
						renderInput={(params) => <TextField {...params} label="Customer" />}
					/>
				</Grid2>
				<Grid2 size={{ xs: 2 }}>
					<Autocomplete
						size="small"
						options={[ALL_SECTION, ...sections]}
						getOptionLabel={(opt) => opt.SectionName}
						value={section ?? ALL_SECTION}
						isOptionEqualToValue={(opt, val) => opt.SectionId === val.SectionId}
						onChange={(_, val) => {
							const mapped = !val || val.SectionId === -1 ? null : val;
							setSection(mapped);
							notify({ section: mapped });
						}}
						renderInput={(params) => <TextField {...params} label="Section" />}
					/>
				</Grid2>
				<Grid2 size={{ xs: 2 }}>
					<Autocomplete
						size="small"
						options={[ALL_GRADE, ...(grades || [])]}
						getOptionLabel={(opt) => opt.GradeName}
						value={grade ?? ALL_GRADE}
						isOptionEqualToValue={(opt, val) => opt.GradeId === val.GradeId}
						onChange={(_, val) => {
							const mapped = !val || val.GradeId === -1 ? null : val;
							setGrade(mapped);
							notify({ gradeName: mapped?.GradeName ?? null });
						}}
						renderInput={(params) => <TextField {...params} label="Grade" />}
					/>
				</Grid2>
				<Grid2 size={{ xs: 3 }}>
					<Autocomplete
						multiple
						disableCloseOnSelect
						size="small"
						options={STATUS_OPTIONS}
						value={statuses}
						onChange={(_, val) => {
							setStatuses(val);
							notify({ statuses: val });
						}}
						renderOption={({ key, ...props }, option, { selected }) => {
							const colors = STATUS_COLORS[option] || STATUS_COLORS.Pending;
							return (
								<li key={key} {...props}>
									<Checkbox
										size="small"
										checked={selected}
										sx={{ mr: 0.5, p: 0 }}
									/>
									<Box
										component="span"
										sx={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											backgroundColor: colors.color,
											mr: 0.75,
											flexShrink: 0,
										}}
									/>
									{option}
								</li>
							);
						}}
						renderTags={(value) => (
							<span style={{ fontSize: "0.875rem" }}>{value.join(", ")}</span>
						)}
						renderInput={(params) => <TextField {...params} label="Status" />}
					/>
				</Grid2>
				<Grid2 size="auto" sx={{ display: "flex", alignItems: "center" }}>
					<Button
						variant="outlined"
						onClick={clearFilters}
						disabled={!hasFilters}
						startIcon={<FilterListOffIcon />}
						sx={{
							whiteSpace: "nowrap",
							textTransform: "none",
							height: 38,
							marginTop: "3px",
						}}
					>
						Reset Filters
					</Button>
				</Grid2>
			</Grid2>
		</Paper>
	);
};
